import { DateTime } from 'luxon'

import type {
  LocalPqRun,
  PqMarketCatalogueEntry,
  PqMarketProgram,
  PqTestName,
} from '@/features/prequalification/localControl/types'
import { runsInMarket, testsOfMarket } from '@/features/prequalification/localControl/utils/marketCatalogue'
import type { AggregatePhase, PqMarketStatus, PqPhase } from '@/features/prequalification/types'
import { aggregatePhase } from '@/features/prequalification/types'

/**
 * Client-side rollups of a resource set's runs into the per-test and per-market shapes
 * the detail page renders. The backend exposes only flat per-(resource, test)
 * runs; everything here is derived from them.
 *
 * A test counts as *passed* only when **every** resource in the resource set passed it.
 */

/**
 * Collapse a run history down to the *current* run for each (resource, test).
 *
 * Re-running a test ("Queue again") creates a brand-new run rather than replacing
 * the previous one, so the backend's `/runs` list accumulates every attempt. The
 * UI only ever cares about the latest attempt per (resource, test): aggregating
 * over the raw history lets a stale, superseded run (e.g. one left `SCHEDULED`
 * because it never started) outrank a finished re-run — `aggregatePhase` ranks
 * `SCHEDULED` above the terminal phases — and make a finished market look
 * unfinished. Deduping here keeps the headers, counts and per-resource pips all
 * consistent with the run actually shown for each (resource, test).
 *
 * "Latest" is the most recently *requested* attempt — `requestedAt` is stamped when a
 * run is queued and never changes, and a re-run is always queued later, so it identifies
 * the current attempt more reliably than `updatedAt` (which can move on an older run). This
 * matches the backend's resource-set summary rollup (LC-313). `updatedAt` then `id` break
 * ties for determinism.
 */
export function latestRunsPerResourceTest(runs: LocalPqRun[]): LocalPqRun[] {
  const latestByKey = new Map<string, LocalPqRun>()

  for (const run of runs) {
    const key = `${run.resourceId}::${run.testName}`
    const current = latestByKey.get(key)
    if (!current || isMoreRecentAttempt(run, current)) latestByKey.set(key, run)
  }

  return [...latestByKey.values()]
}

/** Whether `candidate` is a later attempt than `incumbent`: by requestedAt, then updatedAt, then id. */
function isMoreRecentAttempt(candidate: LocalPqRun, incumbent: LocalPqRun): boolean {
  if (!candidate.requestedAt.equals(incumbent.requestedAt)) return candidate.requestedAt > incumbent.requestedAt
  if (!candidate.updatedAt.equals(incumbent.updatedAt)) return candidate.updatedAt > incumbent.updatedAt
  return candidate.id > incumbent.id
}

/**
 * The runs that actually back a resource set's *current* view: the current run per
 * (resource, test) for resources that are still in the set.
 *
 * The backend keeps a resource's run history even after it's removed from the set, so
 * `/runs` can return runs for resources the detail page no longer lists. Counting those
 * orphaned runs in a rollup makes the header phase disagree with the rows — e.g. a removed
 * resource's `FAILED` runs drive the market to "Failed" while every visible row passed.
 * Scoping to the live membership (and collapsing re-runs via `latestRunsPerResourceTest`)
 * keeps the rollups consistent with what the table renders.
 */
export function currentRunsForResources(runs: LocalPqRun[], resourceIds: string[]): LocalPqRun[] {
  const memberIds = new Set(resourceIds)
  return latestRunsPerResourceTest(runs.filter((run) => memberIds.has(run.resourceId)))
}

export type TestAggregate = {
  testName: PqTestName
  /** Aggregate phase across the resources that have a run for this test (`NOT_STARTED` until one does). */
  phase: AggregatePhase
  /** Whether any resource has a run for this test yet. */
  started: boolean
  /** Resources whose run for this test reached PASSED. */
  passed: number
  /** Resources in the resource set (the denominator of "{passed}/{total} passed"). */
  total: number
  /** True only when all resource set resources passed this test. */
  isPassed: boolean
  /** The runs backing this test, one per resource that has been triggered. */
  runs: LocalPqRun[]
}

export type MarketAggregate = {
  market: PqMarketProgram
  /** Aggregate phase across every run in the market (`NOT_STARTED` while it has none). */
  phase: AggregatePhase
  /** Whether the market has any runs yet. */
  hasRuns: boolean
  /** In-market tests that every resource set resource passed. */
  passedTests: number
  /** Total in-market tests (`testsOfMarket(market).length`). */
  totalTests: number
  /** When the market's next still-scheduled run starts; `null` when nothing is scheduled. */
  scheduledStartAt: DateTime | null
}

export function aggregateTest(runs: LocalPqRun[], testName: PqTestName, totalResources: number): TestAggregate {
  const testRuns = latestRunsPerResourceTest(runs.filter((run) => run.testName === testName))
  const passed = testRuns.filter((run) => run.phase === 'PASSED').length

  return {
    testName,
    phase: aggregatePhase(testRuns.map((run) => run.phase)),
    started: testRuns.length > 0,
    passed,
    total: totalResources,
    isPassed: totalResources > 0 && passed === totalResources,
    runs: testRuns,
  }
}

export function aggregateMarket(
  runs: LocalPqRun[],
  market: PqMarketCatalogueEntry,
  totalResources: number,
): MarketAggregate {
  const tests = testsOfMarket(market)
  const marketRuns = latestRunsPerResourceTest(runsInMarket(runs, market))
  const passedTests = tests.filter((testName) => aggregateTest(marketRuns, testName, totalResources).isPassed).length

  return {
    market: market.market,
    phase: aggregatePhase(marketRuns.map((run) => run.phase)),
    hasRuns: marketRuns.length > 0,
    passedTests,
    totalTests: tests.length,
    scheduledStartAt: earliestScheduledStart(marketRuns),
  }
}

/**
 * The soonest start time among the still-scheduled runs, or `null` when none is scheduled.
 *
 * A market reads as `SCHEDULED` because some run is waiting to start, so the earliest of
 * those start times is what answers "when does this begin?" for the rollup.
 */
function earliestScheduledStart(runs: LocalPqRun[]): DateTime | null {
  const startTimes = runs.flatMap((run) => (run.phase === 'SCHEDULED' && run.startAt ? [run.startAt] : []))

  // `DateTime.min` is undefined for an empty spread — a market with nothing scheduled.
  return DateTime.min(...startTimes) ?? null
}

/**
 * A resource set's headline phase, derived from its per-market rollups: the
 * highest-priority phase across the markets it has runs in, or `NOT_STARTED`
 * when it has none.
 *
 * This is the single source of truth for a set's overall phase. Both the list
 * summary (which carries only the market rollups) and the detail header (which
 * has the full run history) resolve through it, so an untouched set never reads
 * as "Scheduled" in one view and "Not started" in the other. Aggregating the
 * per-market aggregates yields the same phase as aggregating the raw runs —
 * phase priority is a total order, so the max-of-maxes equals the global max.
 */
export function resourceSetPhase(markets: PqMarketStatus[]): AggregatePhase {
  const runPhases = markets.map((status) => status.phase).filter((phase): phase is PqPhase => phase !== 'NOT_STARTED')
  return aggregatePhase(runPhases)
}

/**
 * Markets that have at least one run, each with its aggregate phase, in catalogue
 * order. Drives the list grid's per-market outcome chips (a market only appears
 * once a test in it has been triggered).
 */
export function marketStatuses(runs: LocalPqRun[], markets: PqMarketCatalogueEntry[]): PqMarketStatus[] {
  return markets.flatMap((market): PqMarketStatus[] => {
    const marketRuns = latestRunsPerResourceTest(runsInMarket(runs, market))
    if (marketRuns.length === 0) return []
    return [{ market: market.market, phase: aggregatePhase(marketRuns.map((run) => run.phase)) }]
  })
}
